import React from "react";
import classes from "./rating.module.scss";

interface RatingStarsProps {
    /** Current value (0-5, fractional supported for display). */
    value: number;
    /** Number of stars (default 5). */
    max?: number;
    /** Star size in pixels. */
    size?: number;
    /** When true, allows the user to click a star to set the value. */
    interactive?: boolean;
    /** Called with the new value (0.5 increments, 0.5..max) when the user clicks. */
    onChange?: (value: number) => void;
    /** Disables interaction visually. */
    disabled?: boolean;
    /** Optional aria-label override. */
    ariaLabel?: string;
}

/**
 * Display + optional input for a 0-5 star rating. Uses bootstrap-icons (bi-star,
 * bi-star-half, bi-star-fill) and theme-aware classes from rating.module.scss.
 */
const RatingStars: React.FC<RatingStarsProps> = ({
    value,
    max = 5,
    size = 18,
    interactive = false,
    onChange,
    disabled = false,
    ariaLabel,
}) => {
    const safe = Math.max(0, Math.min(max, Number(value) || 0));

    const renderStar = (index: number) => {
        const starValue = index + 1;
        let iconCls = "bi bi-star";
        let colorCls = classes.starEmpty;
        if (safe >= starValue) {
            iconCls = "bi bi-star-fill";
            colorCls = classes.starFilled;
        } else if (safe >= starValue - 0.5) {
            iconCls = "bi bi-star-half";
            colorCls = classes.starFilled;
        }

        /** Click on the left half of a star sets a half-step value
         *  (e.g. clicking left of star 4 → 3.5), right half sets the full value. */
        const handleClick = (e: React.MouseEvent<HTMLElement>) => {
            if (!interactive || disabled || !onChange) return;
            const rect = e.currentTarget.getBoundingClientRect();
            const isLeftHalf = e.clientX - rect.left < rect.width / 2;
            onChange(isLeftHalf ? starValue - 0.5 : starValue);
        };

        const baseStyle: React.CSSProperties = {
            fontSize: size,
            marginRight: 2,
            cursor: interactive && !disabled ? "pointer" : "default",
            opacity: disabled ? 0.5 : undefined,
        };

        return (
            <i
                key={index}
                className={`${iconCls} ${colorCls}`}
                style={baseStyle}
                onClick={handleClick}
                role={interactive ? "button" : undefined}
                tabIndex={interactive && !disabled ? 0 : undefined}
                onKeyDown={(e) => {
                    if (interactive && !disabled && onChange && (e.key === "Enter" || e.key === " ")) {
                        e.preventDefault();
                        // Keyboard always sets full star — half-step via click only.
                        onChange(starValue);
                    }
                }}
                aria-label={`${starValue} of ${max} stars`}
                title={interactive && !disabled ? "Click left half for ½ star" : undefined}
            />
        );
    };

    return (
        <span aria-label={ariaLabel ?? `Rating: ${safe.toFixed(1)} of ${max}`} role="img">
            {Array.from({ length: max }, (_, i) => renderStar(i))}
        </span>
    );
};

export default RatingStars;
